import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Center, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { Lock } from 'lucide-react';
import { IconBrandWhatsapp } from '@tabler/icons-react';
import { waLink } from './FeatureLockedModal';
import { moduleForPath } from '@constants/modules';
import { useCompanyFeatures } from '@contexts/CompanyFeaturesContext';
import { useTheme } from '@contexts/ThemeContext';

/**
 * Route-level counterpart of the sidebar locks: catches direct URL hits into
 * a disabled module and replaces the page with a locked state. Sits around
 * the dashboard <Outlet /> so individual routes need no changes.
 */
export default function ModuleGuard({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { isFeatureEnabled, isLoading } = useCompanyFeatures();

  const module = moduleForPath(pathname);

  // Fail-open while loading — never flash a lock at users whose modules are on.
  if (!module || isLoading || isFeatureEnabled(module.code)) {
    return <>{children}</>;
  }

  return <FeatureLockedPage moduleLabel={module.sidebarLabel} />;
}

function FeatureLockedPage({ moduleLabel }: { moduleLabel: string }) {
  const { colors: themeColors } = useTheme();

  return (
    <Center style={{ minHeight: '60vh' }}>
      <Stack align="center" gap="md" maw={420} px="md">
        <ThemeIcon size={72} radius="xl" variant="light" color="gray">
          <Lock size={36} />
        </ThemeIcon>
        <Title order={3} ta="center">
          Fitur Belum Aktif
        </Title>
        <Text size="sm" ta="center" c={themeColors.textSecondary}>
          Modul <b>{moduleLabel}</b> belum aktif untuk perusahaan Anda. Silakan hubungi Admin Lumen untuk mengaktifkan
          fitur ini dan membahas pembayaran.
        </Text>
        <Button
          component="a"
          href={waLink(moduleLabel)}
          target="_blank"
          rel="noopener noreferrer"
          color="#25D366"
          variant="filled"
          radius="md"
          leftSection={<IconBrandWhatsapp size={18} />}
        >
          Hubungi Customer Service
        </Button>
      </Stack>
    </Center>
  );
}
